import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import React, { useEffect } from 'react';
import ErrorMessage from '../../../components/common/ErrorMessage';
import FormField from '@/components/UI/FormField';
import { RolesTypes, showToastNotification } from '@/utility/helper';
import { useForm } from 'react-hook-form';
import { errorTypes } from '@/utility/validation';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import { callApi } from '@/api/callApi';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';

interface Permission {
  resource: string;
  actions: string[];
}

interface RoleForm {
  name: string;
  [key: string]: any;
}

const alwaysChecked = ['profile', 'cart', 'address'];

const AddRoles: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset,
  } = useForm<RoleForm>({
    defaultValues: {
      name: '',
      permissions: {},
    },
  });

  const watchAll = watch();

  const onSubmit = async (data: RoleForm) => {
    try {
      const permissions: Permission[] = RolesTypes.map(
        ({ resource, actions }) => {
          const selectedActions = actions.filter(
            (action) => watchAll[`${resource}-${action}`],
          );
          return selectedActions.length
            ? { resource, actions: selectedActions }
            : null;
        },
      ).filter(Boolean) as Permission[];

      const finalData = { name: data.name, permissions };
      let PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.roles}`;
      if (id) PATH = `${PATH}/${id}`;
      await callApi(PATH, { method: id ? 'PATCH' : 'POST', data: finalData });
      navigate(RoutesLink.roles);
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.error('error in role submit', error);
    }
  };

  const fetchRoleData = async () => {
    try {
      const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.roles}/${id}`;
      const res = await callApi(PATH, { method: 'GET' });
      const defaultValues: RoleForm = {
        name: res.name,
      };
      res.permissions.forEach((perm: Permission) => {
        perm.actions.forEach((action) => {
          const key = `${perm.resource}-${action}`;
          defaultValues[key] = true;
        });
      });

      reset(defaultValues);

      // Ensure alwaysChecked resources are set to true after reset
      RolesTypes.forEach(({ resource, actions }) => {
        if (alwaysChecked.includes(resource)) {
          actions.forEach((action) => {
            const key = `${resource}-${action}`;
            setValue(key, true);
          });
        }
      });
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.error('error in role fetch', error);
    }
  };

  const handleSelectRole = (
    resource: string,
    actions: string[],
    checked: boolean,
  ) => {
    // Prevent unchecking for alwaysChecked resources
    if (alwaysChecked.includes(resource) && !checked) {
      return;
    }

    actions.forEach((action) => {
      const key = `${resource}-${action}`;
      setValue(key, checked);
    });
  };

  const handleSelectAll = (checked: boolean) => {
    RolesTypes.forEach(({ resource, actions }) => {
      actions.forEach((action) => {
        const key = `${resource}-${action}`;
        setValue(key, checked);
      });
    });

    // Re-check alwaysChecked resources if unchecking all
    if (!checked) {
      RolesTypes.forEach(({ resource, actions }) => {
        if (alwaysChecked.includes(resource)) {
          actions.forEach((action) => {
            const key = `${resource}-${action}`;
            setValue(key, true);
          });
        }
      });
    }
  };

  useEffect(() => {
    // Set alwaysChecked permissions to true on mount
    RolesTypes.forEach(({ resource, actions }) => {
      if (alwaysChecked.includes(resource)) {
        actions.forEach((action) => {
          const key = `${resource}-${action}`;
          setValue(key, true);
        });
      }
    });

    if (id) fetchRoleData();
  }, [id]);

  return (
    <Layout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Add Role</h3>
        </div>

        <Card>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <FormField
                label="Role Name"
                placeholder="Enter role name"
                {...register('name', {
                  ...errorTypes.required('Role Name'),
                })}
              />
              <ErrorMessage message={errors.name?.message} />
            </div>

            <div className="mb-4">
              <label className="form-label">Permissions</label>

              <div className="mb-3">
                <FormField
                  type="checkbox"
                  label="Select All Permissions"
                  checked={RolesTypes.every(({ resource, actions }) =>
                    actions.every((perm) => watchAll[`${resource}-${perm}`]),
                  )}
                  onChange={(e: any) => handleSelectAll(e.target.checked)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {RolesTypes.map(({ resource, actions }) => (
                  <div key={resource} className="mb-4">
                    <div className="rounded-lg shadow-sm overflow-hidden">
                      <div className="card-header bg-primary-light px-3 py-2">
                        <FormField
                          type="checkbox"
                          label={resource}
                          checked={actions.every(
                            (perm) => watchAll[`${resource}-${perm}`],
                          )}
                          disabled={alwaysChecked.includes(resource)}
                          onChange={(e: any) =>
                            handleSelectRole(
                              resource,
                              actions,
                              e.target.checked,
                            )
                          }
                        />
                      </div>

                      <div className="card-body px-3 py-2">
                        <div className="flex flex-wrap gap-3">
                          {actions.map((perm) => {
                            const fieldKey = `${resource}-${perm}`;
                            return (
                              <FormField
                                key={fieldKey}
                                type="checkbox"
                                {...register(fieldKey)}
                                label={perm}
                                disabled={alwaysChecked.includes(resource)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary text-white px-5"
              >
                {isSubmitting && <BtnLoader />} Save Role
              </button>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default AddRoles;
